import React, { useEffect, useState, useRef, lazy } from 'react';
import { VideoHero } from '../components/VideoHero';
import { Section } from '../components/Section';
import { Button } from '../components/ui/Button';
import { Separator } from '../components/ui/Separator';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Label } from '../components/ui/Label';
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return {
    ref,
    isVisible
  };
}
function FadeIn({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useInView();
  return <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-196">
      {children}
    </div>;
}
const DESTINATION_IMAGES = {
  europe: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=800&q=80',
  middleEast: 'https://images.unsplash.com/photo-1547234935-80c7145ec969?w=800&q=80',
  asia: 'https://images.unsplash.com/photo-1528164344705-47542687000d?w=800&q=80',
  africa: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=800&q=80'
};
const BEYOND_IMAGES = {
  iraq: 'https://images.unsplash.com/photo-1603565816030-6b389eeb23cb?w=600&q=80',
  iran: 'https://images.unsplash.com/photo-1565711561500-49678a10a63f?w=600&q=80',
  afghanistan: 'https://images.unsplash.com/photo-1561361513-2d000a50f0dc?w=600&q=80',
  yemen: 'https://images.unsplash.com/photo-1578895101408-1a36b834405b?w=600&q=80',
  somalia: 'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=600&q=80',
  northKorea: 'https://images.unsplash.com/photo-1590559899731-a382839e5549?w=600&q=80'
};
export function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interests: '',
    dates: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        interests: '',
        dates: '',
        notes: ''
      });
    }, 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full" data-id="element-197">
      {/* ─── HERO ─── */}
      <section id="hero" aria-label="Hero - Travel Curated Without Compromise" data-id="element-198">
        <VideoHero data-id="element-199">
          <div className="flex flex-col items-center justify-end min-h-screen px-6 pb-24 md:pb-32" data-id="element-200">
            <div className="max-w-4xl mx-auto text-center text-white" data-id="element-201">
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] font-normal mb-6 leading-[1.1] tracking-tight" data-id="element-202">
                Travel, Curated Without Compromise
              </h1>
              <p className="text-base md:text-lg mb-10 leading-relaxed max-w-2xl mx-auto font-light text-white/90" data-id="element-203">
                Bespoke journeys designed with intelligence, intuition, and
                insider access — for those who travel beyond the obvious.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-204">
                <button onClick={() => scrollTo('#contact')} className="inline-flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-white border border-white/50 px-8 py-4 hover:bg-white hover:text-foreground transition-all duration-300" data-id="element-205">
                  Start Your Journey
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-206">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-207" />
                  </svg>
                </button>
                <button onClick={() => scrollTo('#philosophy')} className="inline-flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-white/80 px-8 py-4 hover:text-white transition-all duration-300" data-id="element-208">
                  Explore Our Philosophy
                </button>
              </div>
            </div>
            <div className="mt-12 animate-bounce" data-id="element-209">
              <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-210">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" data-id="element-211" />
              </svg>
            </div>
          </div>
        </VideoHero>
      </section>

      {/* ─── ABOUT with image ─── */}
      <section id="about" aria-label="About 3 DOTS Travel" data-id="element-212">
        <Section padding="large" data-id="element-213">
          <div className="max-w-7xl mx-auto" data-id="element-214">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-215">
              <FadeIn data-id="element-216">
                <div className="relative overflow-hidden" data-id="element-217">
                  <img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80" alt="Aerial view of a turquoise lake surrounded by lush green mountains, representing the pristine destinations curated by 3 DOTS Travel" loading="lazy" className="w-full h-[400px] lg:h-[550px] object-cover" data-id="element-218" />
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/20 to-transparent" data-id="element-219"></div>
                </div>
              </FadeIn>
              <div data-id="element-220">
                <FadeIn data-id="element-221">
                  <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent mb-6" data-id="element-222">
                    Who We Are
                  </p>
                </FadeIn>
                <FadeIn delay={100} data-id="element-223">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-primary mb-8 leading-tight tracking-tight" data-id="element-224">
                    A Boutique Travel Atelier
                  </h2>
                </FadeIn>
                <FadeIn delay={200} data-id="element-225">
                  <p className="text-lg text-muted-foreground leading-relaxed mb-6" data-id="element-226">
                    3 DOTS Travel is a privately owned travel design studio
                    creating highly personalized journeys across the world. We
                    work with a limited number of clients each year, allowing us
                    to focus on depth, detail, and exceptional execution.
                  </p>
                </FadeIn>
                <FadeIn delay={300} data-id="element-227">
                  <p className="text-lg text-muted-foreground leading-relaxed mb-6" data-id="element-228">
                    Rooted in decades of hands-on experience and trusted global
                    relationships, our work goes far beyond logistics. We design
                    journeys shaped by people, place, and purpose.
                  </p>
                </FadeIn>
                <FadeIn delay={400} data-id="element-229">
                  <p className="text-lg font-medium text-primary" data-id="element-230">
                    We don't sell itineraries. We craft experiences that
                    resonate long after the journey ends.
                  </p>
                </FadeIn>
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* ─── FULL-WIDTH IMAGE BREAK ─── */}
      <div className="w-full h-[40vh] md:h-[50vh] relative overflow-hidden" role="img" aria-label="Crystal clear tropical beach with turquoise waters" data-id="element-231">
        <img src="https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1600&q=80" alt="Crystal clear tropical beach with turquoise waters and white sand, showcasing the exotic beach destinations available through 3 DOTS Travel" loading="lazy" className="w-full h-full object-cover" data-id="element-232" />
        <div className="absolute inset-0 bg-primary/20" data-id="element-233"></div>
      </div>

      {/* ─── PHILOSOPHY ─── */}
      <section id="philosophy" aria-label="Our Travel Philosophy" data-id="element-234">
        <Section background="muted" padding="large" data-id="element-235">
          <div className="max-w-6xl mx-auto" data-id="element-236">
            <FadeIn data-id="element-237">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent text-center mb-6" data-id="element-238">
                Our Philosophy
              </p>
            </FadeIn>
            <FadeIn delay={100} data-id="element-239">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-primary text-center mb-6 tracking-tight" data-id="element-240">
                Travel, Done Thoughtfully
              </h2>
            </FadeIn>
            <FadeIn delay={200} data-id="element-241">
              <p className="text-lg text-center text-muted-foreground mb-16 max-w-3xl mx-auto" data-id="element-242">
                We believe the most meaningful travel happens where curiosity
                meets understanding.
              </p>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12 mb-16" data-id="element-243">
              <FadeIn delay={100} data-id="element-244">
                <div className="space-y-3" data-id="element-245">
                  <div className="w-10 h-[2px] bg-accent mb-5" data-id="element-246"></div>
                  <h3 className="text-lg font-medium text-primary uppercase tracking-wide" data-id="element-247">
                    Genuine Human Connection
                  </h3>
                  <p className="text-muted-foreground leading-relaxed" data-id="element-248">
                    Travel is about people. We facilitate authentic encounters
                    with local communities, artisans, and cultural guardians who
                    share their world with warmth.
                  </p>
                </div>
              </FadeIn>
              <FadeIn delay={200} data-id="element-249">
                <div className="space-y-3" data-id="element-250">
                  <div className="w-10 h-[2px] bg-accent mb-5" data-id="element-251"></div>
                  <h3 className="text-lg font-medium text-primary uppercase tracking-wide" data-id="element-252">
                    Cultural Respect
                  </h3>
                  <p className="text-muted-foreground leading-relaxed" data-id="element-253">
                    We approach every destination with humility and reverence,
                    ensuring our presence enriches rather than disrupts the
                    communities we visit.
                  </p>
                </div>
              </FadeIn>
              <FadeIn delay={300} data-id="element-254">
                <div className="space-y-3" data-id="element-255">
                  <div className="w-10 h-[2px] bg-accent mb-5" data-id="element-256"></div>
                  <h3 className="text-lg font-medium text-primary uppercase tracking-wide" data-id="element-257">
                    Seamless Logistics
                  </h3>
                  <p className="text-muted-foreground leading-relaxed" data-id="element-258">
                    Every detail is anticipated and handled with precision,
                    allowing you to be fully present in each moment.
                  </p>
                </div>
              </FadeIn>
              <FadeIn delay={400} data-id="element-259">
                <div className="space-y-3" data-id="element-260">
                  <div className="w-10 h-[2px] bg-accent mb-5" data-id="element-261"></div>
                  <h3 className="text-lg font-medium text-primary uppercase tracking-wide" data-id="element-262">
                    Unrepeatable Experiences
                  </h3>
                  <p className="text-muted-foreground leading-relaxed" data-id="element-263">
                    We create moments designed specifically for you — private
                    access, exclusive encounters, experiences that cannot be
                    rushed or replicated.
                  </p>
                </div>
              </FadeIn>
            </div>
            <FadeIn delay={500} data-id="element-264">
              <p className="font-serif text-2xl md:text-3xl text-center text-primary italic leading-relaxed" data-id="element-265">
                Our role is not to impress — but to open doors.
              </p>
            </FadeIn>
          </div>
        </Section>
      </section>

      {/* ─── DESTINATIONS with images ─── */}
      <section id="destinations" aria-label="Travel Destinations" data-id="element-266">
        <Section padding="large" data-id="element-267">
          <div className="max-w-7xl mx-auto" data-id="element-268">
            <FadeIn data-id="element-269">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent text-center mb-6" data-id="element-270">
                Destinations
              </p>
            </FadeIn>
            <FadeIn delay={100} data-id="element-271">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-primary text-center mb-6 tracking-tight" data-id="element-272">
                Where We Travel
              </h2>
            </FadeIn>
            <FadeIn delay={200} data-id="element-273">
              <p className="text-center text-muted-foreground mb-16 text-lg" data-id="element-274">
                Bespoke journeys guided by depth, authenticity, and access.
              </p>
            </FadeIn>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-275">
              <FadeIn delay={100} data-id="element-276">
                <div className="group relative overflow-hidden cursor-pointer" data-id="element-277">
                  <img src={DESTINATION_IMAGES.europe} alt="Venice canal with gondolas and historic Italian architecture — representing bespoke European travel through Slovenia, Italy, and the Mediterranean" loading="lazy" className="w-full h-[320px] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-278" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-279"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-280">
                    <h3 className="text-2xl font-medium text-white mb-2" data-id="element-281">
                      Europe
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed" data-id="element-282">
                      Slovenia, Italy, Balkans, Mediterranean — experience
                      Europe beyond the guidebook.
                    </p>
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={200} data-id="element-283">
                <div className="group relative overflow-hidden cursor-pointer" data-id="element-284">
                  <img src={DESTINATION_IMAGES.middleEast} alt="Ancient desert landscape with traditional Middle Eastern architecture — curated journeys through Jordan, Georgia, Armenia, and Iran" loading="lazy" className="w-full h-[320px] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-285" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-286"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-287">
                    <h3 className="text-2xl font-medium text-white mb-2" data-id="element-288">
                      Middle East & Caucasus
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed" data-id="element-289">
                      Jordan, Georgia, Armenia, Iran — ancient civilizations and
                      profound hospitality.
                    </p>
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={300} data-id="element-290">
                <div className="group relative overflow-hidden cursor-pointer" data-id="element-291">
                  <img src={DESTINATION_IMAGES.asia} alt="Serene Asian temple surrounded by misty mountains — immersive travel experiences across Japan, Central Asia, and Southeast Asia" loading="lazy" className="w-full h-[320px] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-292" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-293"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-294">
                    <h3 className="text-2xl font-medium text-white mb-2" data-id="element-295">
                      Asia
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed" data-id="element-296">
                      Japan, Central Asia, Southeast Asia — spiritual depth and
                      culinary mastery.
                    </p>
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={400} data-id="element-297">
                <div className="group relative overflow-hidden cursor-pointer" data-id="element-298">
                  <img src={DESTINATION_IMAGES.africa} alt="Vast African savanna at golden hour with acacia trees — transformative wilderness encounters in select African destinations" loading="lazy" className="w-full h-[320px] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-299" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-301">
                    <h3 className="text-2xl font-medium text-white mb-2" data-id="element-302">
                      Africa & Beyond
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed" data-id="element-303">
                      Select destinations designed with care — vast wilderness
                      and transformative encounters.
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>

            <FadeIn delay={500} data-id="element-304">
              <p className="text-center text-sm text-muted-foreground italic mt-12 uppercase tracking-widest" data-id="element-305">
                Each itinerary is created individually — never templated, never
                repeated.
              </p>
            </FadeIn>
          </div>
        </Section>
      </section>

      {/* ─── FULL-WIDTH IMAGE BREAK ─── */}
      <div className="w-full h-[40vh] md:h-[50vh] relative overflow-hidden" role="img" aria-label="Tropical sunset over the ocean" data-id="element-306">
        <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=80" alt="Golden tropical sunset over a calm ocean with palm-lined beach, evoking the serenity of bespoke travel experiences" loading="lazy" className="w-full h-full object-cover" data-id="element-307" />
        <div className="absolute inset-0 bg-black/50" data-id="element-308"></div>
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-309">
          <FadeIn data-id="element-310">
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-white text-center italic leading-relaxed max-w-3xl px-6 drop-shadow-lg" data-id="element-311">
              "The world is a book, and those who do not travel read only one
              page."
            </p>
          </FadeIn>
        </div>
      </div>

      {/* ─── BEYOND THE COMFORT ZONE with images ─── */}
      <section id="beyond" aria-label="Beyond the Comfort Zone - Unconventional Destinations" data-id="element-312">
        <Section background="dark" padding="large" data-id="element-313">
          <div className="max-w-6xl mx-auto" data-id="element-314">
            <FadeIn data-id="element-315">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent text-center mb-6" data-id="element-316">
                Beyond the Comfort Zone
              </p>
            </FadeIn>
            <FadeIn delay={100} data-id="element-317">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-center mb-4 tracking-tight" data-id="element-318">
                For the Curious. The Courageous. The Inquisitive.
              </h2>
            </FadeIn>
            <FadeIn delay={200} data-id="element-319">
              <p className="text-lg text-center text-primary-foreground/80 mb-16 max-w-3xl mx-auto leading-relaxed" data-id="element-320">
                Carefully managed, ethically guided experiences in destinations
                often misunderstood or rarely explored.
              </p>
            </FadeIn>

            <FadeIn delay={300} data-id="element-321">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16" data-id="element-322">
                {[{
                name: 'Iraq',
                desc: 'Mesopotamian heritage',
                img: BEYOND_IMAGES.iraq,
                altText: 'Ancient Mesopotamian ruins and architecture in Iraq — guided cultural heritage tours through the cradle of civilization'
              }, {
                name: 'Iran',
                desc: 'Persian culture & warmth',
                img: BEYOND_IMAGES.iran,
                altText: 'Ornate Persian mosque with intricate tilework in Iran — immersive journeys through Persian culture and hospitality'
              }, {
                name: 'Afghanistan',
                desc: 'Cultural heritage',
                img: BEYOND_IMAGES.afghanistan,
                altText: 'Dramatic mountain landscape in Afghanistan — responsibly guided cultural heritage expeditions'
              }, {
                name: 'Yemen',
                desc: 'Living history',
                img: BEYOND_IMAGES.yemen,
                altText: 'Traditional Yemeni architecture with distinctive tower houses — carefully managed journeys through living history'
              }, {
                name: 'Somalia',
                desc: 'Resilient cultures',
                img: BEYOND_IMAGES.somalia,
                altText: 'Coastal landscape of Somalia — ethically guided exploration of resilient cultures and communities'
              }, {
                name: 'North Korea',
                desc: 'Educational journeys',
                img: BEYOND_IMAGES.northKorea,
                altText: "Monumental architecture in North Korea — educational journeys offering rare insight into one of the world's most secluded nations"
              }].map(dest => <div key={dest.name} className="group relative overflow-hidden" data-id="element-323">
                    <img src={dest.img} alt={dest.altText} loading="lazy" className="w-full h-[200px] md:h-[240px] object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-324" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-325"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-326">
                      <h3 className="text-base font-medium text-white mb-1" data-id="element-327">
                        {dest.name}
                      </h3>
                      <p className="text-xs text-white/70" data-id="element-328">{dest.desc}</p>
                    </div>
                  </div>)}
              </div>
            </FadeIn>

            <FadeIn delay={400} data-id="element-329">
              <div className="max-w-3xl mx-auto text-center space-y-4" data-id="element-330">
                <p className="text-sm text-primary-foreground/80 leading-relaxed" data-id="element-331">
                  These are not thrill-seeking adventures. They are deeply
                  researched, responsibly operated journeys built on trust,
                  discretion, and local partnership.
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-accent font-medium" data-id="element-332">
                  Safety · Cultural Respect · Ethical Engagement
                </p>
              </div>
            </FadeIn>
          </div>
        </Section>
      </section>

      {/* ─── WHAT SETS US APART ─── */}
      <Section padding="large" data-id="element-333">
        <div className="max-w-7xl mx-auto" data-id="element-334">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-335">
            <div data-id="element-336">
              <FadeIn data-id="element-337">
                <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent mb-6" data-id="element-338">
                  Why 3 DOTS
                </p>
              </FadeIn>
              <FadeIn delay={100} data-id="element-339">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-primary mb-12 tracking-tight" data-id="element-340">
                  What Sets Us Apart
                </h2>
              </FadeIn>
              {[{
              title: 'Unmatched Access',
              desc: 'Trusted relationships and local expertise open doors few others can.'
            }, {
              title: 'Expert-Led Journeys',
              desc: 'Local fixers, historians, and cultural specialists guide every step.'
            }, {
              title: 'Bespoke by Design',
              desc: 'Every journey is built from the ground up — never recycled.'
            }, {
              title: 'High-Touch Service',
              desc: 'White-glove planning, seamless logistics, and 24/7 on-the-ground support.'
            }, {
              title: 'Discretion & Trust',
              desc: 'Many of our journeys are confidential by nature.'
            }].map((item, i) => <FadeIn key={item.title} delay={i * 100 + 200} data-id="element-341">
                  <div className="mb-8" data-id="element-342">
                    <div className="w-8 h-[2px] bg-accent mb-4" data-id="element-343"></div>
                    <h3 className="text-sm font-medium text-primary uppercase tracking-[0.15em] mb-2" data-id="element-344">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-345">
                      {item.desc}
                    </p>
                  </div>
                </FadeIn>)}
            </div>
            <FadeIn delay={200} data-id="element-346">
              <div className="relative" data-id="element-347">
                <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80" alt="Winding mountain road through dramatic alpine scenery, symbolizing the unique and personalized travel journeys designed by 3 DOTS Travel" loading="lazy" className="w-full h-[500px] lg:h-[650px] object-cover" data-id="element-348" />
                <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black/20 to-transparent" data-id="element-349"></div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* ─── FULL-WIDTH IMAGE BREAK ─── */}
      <div className="w-full h-[40vh] md:h-[50vh] relative overflow-hidden" role="img" aria-label="Misty mountain landscape" data-id="element-350">
        <img src="https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=1600&q=80" alt="Dramatic misty mountain peaks rising above clouds at sunrise, representing the awe-inspiring destinations accessible through 3 DOTS Travel" loading="lazy" className="w-full h-full object-cover" data-id="element-351" />
        <div className="absolute inset-0 bg-primary/20" data-id="element-352"></div>
      </div>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" aria-label="How Our Travel Design Process Works" data-id="element-353">
        <Section background="muted" padding="large" data-id="element-354">
          <div className="max-w-5xl mx-auto" data-id="element-355">
            <FadeIn data-id="element-356">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent text-center mb-6" data-id="element-357">
                The Process
              </p>
            </FadeIn>
            <FadeIn delay={100} data-id="element-358">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-primary text-center mb-16 tracking-tight" data-id="element-359">
                How It Works
              </h2>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-360">
              {[{
              step: '01',
              title: 'Consultation',
              desc: 'A personal conversation to understand your vision, interests, and expectations.'
            }, {
              step: '02',
              title: 'Design',
              desc: 'A bespoke itinerary crafted with intention and precision.'
            }, {
              step: '03',
              title: 'Refinement',
              desc: 'Every detail refined until it feels effortless.'
            }, {
              step: '04',
              title: 'Execution',
              desc: 'Full support before, during, and after your journey.'
            }].map((item, i) => <FadeIn key={item.step} delay={i * 150} data-id="element-361">
                  <div className="text-center" data-id="element-362">
                    <p className="text-4xl font-light text-accent mb-4" data-id="element-363">
                      {item.step}
                    </p>
                    <h3 className="text-sm font-medium text-primary uppercase tracking-[0.15em] mb-3" data-id="element-364">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-365">
                      {item.desc}
                    </p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </Section>
      </section>

      {/* ─── WHO WE WORK WITH ─── */}
      <Section padding="large" data-id="element-366">
        <div className="max-w-7xl mx-auto" data-id="element-367">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-368">
            <FadeIn data-id="element-369">
              <div className="relative overflow-hidden" data-id="element-370">
                <img src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=800&q=80" alt="Traveler overlooking a stunning coastal vista from a clifftop viewpoint, representing the exclusive luxury travel experiences for discerning clients" loading="lazy" className="w-full h-[400px] lg:h-[500px] object-cover" data-id="element-371" />
              </div>
            </FadeIn>
            <div data-id="element-372">
              <FadeIn data-id="element-373">
                <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent mb-6" data-id="element-374">
                  Our Clients
                </p>
              </FadeIn>
              <FadeIn delay={100} data-id="element-375">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-primary mb-10 tracking-tight" data-id="element-376">
                  Who We Work With
                </h2>
              </FadeIn>
              <FadeIn delay={200} data-id="element-377">
                <div className="space-y-6 mb-8" data-id="element-378">
                  {['Seasoned Global Travelers', 'Private Families & Couples', 'Creative Leaders & Entrepreneurs', 'Advisors & Concierges'].map(client => <div key={client} className="flex items-center gap-4" data-id="element-379">
                      <div className="w-8 h-[1px] bg-accent" data-id="element-380"></div>
                      <span className="text-sm font-medium text-primary uppercase tracking-[0.1em]" data-id="element-381">
                        {client}
                      </span>
                    </div>)}
                </div>
              </FadeIn>
              <FadeIn delay={300} data-id="element-382">
                <p className="text-sm text-muted-foreground italic" data-id="element-383">
                  Privacy, trust, and long-term relationships define our work.
                </p>
              </FadeIn>
            </div>
          </div>
        </div>
      </Section>

      {/* ─── CONTACT ─── */}
      <section id="contact" aria-label="Contact 3 DOTS Travel to Plan Your Journey" data-id="element-384">
        <Section background="dark" padding="large" data-id="element-385">
          <div className="max-w-4xl mx-auto" data-id="element-386">
            <FadeIn data-id="element-387">
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-accent text-center mb-6" data-id="element-388">
                Get in Touch
              </p>
            </FadeIn>
            <FadeIn delay={100} data-id="element-389">
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal text-center mb-4 leading-tight" data-id="element-390">
                Let's Design Something Extraordinary
              </h2>
            </FadeIn>
            <FadeIn delay={200} data-id="element-391">
              <p className="text-lg text-center text-primary-foreground/80 mb-16" data-id="element-392">
                Every journey begins with a conversation.
              </p>
            </FadeIn>

            <FadeIn delay={300} data-id="element-393">
              {submitStatus === 'success' ? <div className="text-center py-12" data-id="element-394">
                  <h3 className="text-2xl font-light mb-6" data-id="element-395">Thank You</h3>
                  <p className="text-primary-foreground/80 mb-8" data-id="element-396">
                    We've received your inquiry and will be in touch within 24
                    hours.
                  </p>
                  <button onClick={() => setSubmitStatus('idle')} className="text-xs font-medium uppercase tracking-[0.2em] text-accent hover:text-accent/80 transition-colors" data-id="element-397">
                    Send Another Inquiry
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto" data-id="element-398">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-399">
                    <div data-id="element-400">
                      <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-primary-foreground/80 mb-2 block" data-id="element-401">
                        Name
                      </Label>
                      <Input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className="h-12 bg-transparent border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-none focus:border-accent" placeholder="Your name" data-id="element-402" />
                    </div>
                    <div data-id="element-403">
                      <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-primary-foreground/80 mb-2 block" data-id="element-404">
                        Email
                      </Label>
                      <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="h-12 bg-transparent border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-none focus:border-accent" placeholder="your@email.com" data-id="element-405" />
                    </div>
                  </div>
                  <div data-id="element-406">
                    <Label htmlFor="interests" className="text-xs uppercase tracking-[0.15em] text-primary-foreground/80 mb-2 block" data-id="element-407">
                      Travel Interests
                    </Label>
                    <Input id="interests" name="interests" type="text" value={formData.interests} onChange={handleChange} className="h-12 bg-transparent border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-none focus:border-accent" placeholder="e.g., Cultural immersion, culinary experiences" data-id="element-408" />
                  </div>
                  <div data-id="element-409">
                    <Label htmlFor="dates" className="text-xs uppercase tracking-[0.15em] text-primary-foreground/80 mb-2 block" data-id="element-410">
                      Preferred Dates
                    </Label>
                    <Input id="dates" name="dates" type="text" value={formData.dates} onChange={handleChange} className="h-12 bg-transparent border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-none focus:border-accent" placeholder="e.g., Spring 2025, flexible" data-id="element-411" />
                  </div>
                  <div data-id="element-412">
                    <Label htmlFor="notes" className="text-xs uppercase tracking-[0.15em] text-primary-foreground/80 mb-2 block" data-id="element-413">
                      Your Vision
                    </Label>
                    <Textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} className="min-h-[140px] bg-transparent border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 rounded-none focus:border-accent" placeholder="Share your thoughts about destinations, experiences, or anything that helps us understand your vision..." data-id="element-414" />
                  </div>
                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-xs uppercase tracking-[0.2em] bg-accent hover:bg-accent/90 text-accent-foreground rounded-none h-14" data-id="element-415">
                    {isSubmitting ? 'Sending...' : 'Plan Your Journey'}
                  </Button>
                  <p className="text-xs text-primary-foreground/50 text-center" data-id="element-416">
                    We respond within 24 hours. Your information is kept
                    strictly confidential.
                  </p>
                </form>}
            </FadeIn>
          </div>
        </Section>
      </section>
    </div>;
}